import { useEffect, useRef } from 'react';
import { refreshSession } from './sessionRefresh';

const DEFAULT_INTERVAL_MS = 10 * 60 * 1000;
const INITIAL_DELAY_MS = 30_000;
const MIN_GAP_MS = 60_000;

/**
 * Keep HttpOnly session cookies fresh on long-running surfaces (e.g. report designer).
 * Relies on shared refreshSession de-dupe; app-wide proactive refresh covers most cases.
 * Does not refresh on mount — avoids racing DevExpress initialization.
 */
export function useSessionKeepAlive({ enabled = true, intervalMs = DEFAULT_INTERVAL_MS } = {}) {
  const lastRefreshAtRef = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    const maybeRefresh = async () => {
      const now = Date.now();
      if (now - lastRefreshAtRef.current < MIN_GAP_MS) return;
      lastRefreshAtRef.current = now;
      try {
        await refreshSession();
      } catch {
        // Expired session surfaces on next API call or save attempt.
      }
    };

    const intervalId = window.setInterval(() => {
      if (!cancelled) maybeRefresh();
    }, intervalMs);

    const initialId = window.setTimeout(() => {
      if (!cancelled) maybeRefresh();
    }, INITIAL_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.clearTimeout(initialId);
    };
  }, [enabled, intervalMs]);
}
